import BeamIcon from 'beam/dist/BeamIcon'
import { makeStyles, Tooltip, Divider } from 'beam'
import { MouseEvent, Fragment, KeyboardEvent, ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { LanguageDialog } from './LanguageDialog'
import { logout } from '../LegacyLogin/login.actions'
import Dropdown, { DropdownList, DropdownListItem } from './Dropdown'
import ChangePasswordDialog from '../Password'
import { openDialog as openPasswordDialog } from '../Password/password.actions'
import { useUser } from '../../Permissions'
import { RootState } from '../app.reducer'
import { AuthenticationSource } from '../LegacyLogin/login.constants'
import { datadogRum } from '@datadog/browser-rum'

const truncateName = (tenantName: string): string => {
  if (tenantName.length > 20) {
    return tenantName.substring(0, 17) + '...'
  } else {
    return tenantName
  }
}

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    height: theme.spacing(6),
    fontSize: '14px',
    cursor: 'pointer',
    opacity: 0.7,
    '& span': {
      whiteSpace: 'nowrap',
    },
    fontFamily: theme.typography.fontFamily,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    color: (theme.palette as $TSFIXME).navigationTextColor,
  // fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
  },
  tenantLabel: {
    color: 'rgba(0, 0, 0, 0.54)',
    paddingRight: '5px',
    minWidth: '0',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  listItem: {
    width: '225px',
    fontSize: '14px',
  },
  chevronDown: {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- TODO: remove type assertion
    color: (theme.palette as $TSFIXME).navigationTextColor,
  },
  dropdownList: {
    //todo
  },
}),
)

const gotoFRChangePassword = (authLocation: string, forgerockLoginUrl: string): void => {
  const encodedFRauthLocation =  encodeURIComponent(authLocation)
  window.location.href = `https://${forgerockLoginUrl}/?realm=/bravo&authIndexType=service&authIndexValue=UpdatePassword&noSession=true&ForceAuth=true&goto=${encodedFRauthLocation}`
}

export function UserSettings({ forgerockIdToken = null }: $TSFIXME): ReactElement {
  const dispatch = useDispatch()
  const [isOpen, setIsOpen] = useState(false)
  const [anchor, setAnchor] = useState(null)
  const [isLangDialogOpen, setIsLangDialogOpen] = useState(false)
  const classes = useStyles()
  const { t } = useTranslation()
  const user = useUser()
  const authLocation = useSelector((state: RootState) => state.app.authLocation)
  const frLoginUrl = useSelector((state: RootState) => state.app.frLoginUrl)
  const frOpUrl = useSelector((state: RootState) => state.app.frOpUrl)
  const frRealm = useSelector((state: RootState) => state.app.frRealm)
  const wasAuthFR = useSelector((state: RootState) => state.app.wasAuthFR)
  const showChangePassword = user?.authenticationSource !== AuthenticationSource.OIDC

  function toggleDropdown(event: MouseEvent<$TSFIXME>): void {
    event.preventDefault()
    setIsOpen(!isOpen)
    setAnchor(event.currentTarget)
  }
  function toggleDropdownOnSpaceOrEnter(event: KeyboardEvent<$TSFIXME>): void {
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault()
      setIsOpen(!isOpen)
      setAnchor(event.currentTarget)
    }
  }
  return (
    <Fragment>
      <div
        role="button"
        tabIndex={0}
        className={classes.root}
        onClick={toggleDropdown}
        onKeyUp={toggleDropdownOnSpaceOrEnter}
        data-testid={'UserSettings-button'}
      >
        <span>{t('greeting', { defaultValue: 'Hello, {{name}}', name: user?.firstName })}</span>
        <BeamIcon className={ classes.chevronDown }>ChevronDown</BeamIcon>
      </div>
      <Dropdown open={isOpen} anchor={anchor} onClose={() => setIsOpen(false)}>
        { /* @ts-expect-error TODO: upgrade Dropdown to beam-v11 */ }
        <DropdownList dense className={classes.dropdownList}>
          <DropdownListItem className={classes.listItem}>
            <Tooltip className={classes.tenantLabel} title={user?.tenantName ?? ''} auto-qa-id={'UserSettings-tenant'} data-testid={'UserSettings-tenant'}>
              <span>
                {user?.tenantName ? t('tenant') + ': ' + truncateName(user.tenantName) : t('systemAdministrator')}
              </span>
            </Tooltip>
          </DropdownListItem>
          <Divider />
          {showChangePassword && (
            <DropdownListItem
              auto-qa-id={'UserSettings-changePassword'}
              data-testid={'UserSettings-changePassword'}
              onClick={() =>
                forgerockIdToken ?
                  gotoFRChangePassword(authLocation, frLoginUrl) :
                  dispatch(openPasswordDialog())
              }
              button
            >
              {t('changePassword')}
            </DropdownListItem>
          )}
          <DropdownListItem auto-qa-id={'UserSettings-selectLanguage'} data-testid={'UserSettings-selectLanguage'} onClick={() => setIsLangDialogOpen(true)} button>
            {t('selectLanguage')}
          </DropdownListItem>
          <DropdownListItem
            auto-qa-id={'UserSettings-logout'}
            data-testid={'UserSettings-logout'}
            onClick={() => {
              datadogRum.addAction('Logout called', { caller: 'App/NavBar/UserSettings' })
              dispatch(logout(forgerockIdToken, frLoginUrl, frOpUrl, frRealm, wasAuthFR))
            }}
            button>
            {t('logOut')}
          </DropdownListItem>
        </DropdownList>
      </Dropdown>
      <ChangePasswordDialog />
      <LanguageDialog onClose={() => setIsLangDialogOpen(false)} open={isLangDialogOpen} />
    </Fragment>
  )
}
